/**
 * RobotTrajectoryEditor — 后端机器人轨迹计算函数的前端 TypeScript 移植。
 *
 * 移植来源（viewer/bridge/）：
 *   bridge_core.py    —— 模型/关节信息、轨迹矩阵加载与回写、帧应用
 *   ik_solver.py      —— 双脚调平的 2 关节 Gauss-Newton 求解（仅存活方法）
 *   world_lock_ik.py  —— 世界位姿锁定 IK
 *   foot_leveling.py  —— 双脚调平 / 智能调脚 / 脚部-腰部映射构建
 *   ankle_locked_upright.py —— 锁踝调腰（roll/pitch）
 *   interpolation.py  —— 关键帧插值
 *   zmp_analysis.py   —— ZMP 稳定性风险检测
 *   math_utils.py / utils_rotation.py —— 四元数/旋转工具（见 math.ts）
 *
 * 数据表示与后端一致：number[][] 矩阵，基座列在前（quaternion 基座为 7 列），
 * 四元数内部使用 wxyz。所有特征方法直接读写 this.trajectoryData。
 */

import {
  norm,
  norm3,
  dot,
  clip,
  linearInterp,
  naturalCubicSpline,
  unwrap,
  gradientAxis0,
  quantile,
  smoothVectorsForZmp,
  slerp,
  quatBatchSpline,
  eulerToQuaternion,
  quaternionToEuler,
  rotationMatrixToRotvec,
  solveLinearSystem,
} from './math';

// ===== MuJoCo 枚举常量 =====
const MJOBJ_BODY = 1;
const MJOBJ_JOINT = 3;
const MJJNT_FREE = 0;
const MJJNT_BALL = 1;
const MJJNT_SLIDE = 2;
const MJJNT_HINGE = 3;

type MjModel = any;
type MjData = any;
type MujocoModule = any;

export interface MjHandle {
  model: MjModel;
  data: MjData;
  M: MujocoModule;
  qmap?: Record<string, number>;
}

interface JointInfo {
  jointId: number;
  jointName: string;
  jointRange: [number, number];
  jointType: number;
  qposAddr: number;
  initialValue: number;
  limited: boolean;
}

interface FootSpec {
  side: string;
  pitchJointId: number | null;
  rollJointId: number | null;
  footBodyId: number;
  upAxisIdx: number;
  upAxisSign: number;
}

interface WaistLevelingMap {
  waistPitchJointId: number | null;
  waistRollJointId: number | null;
  torsoBodyId: number;
  hasWaist: boolean;
}

interface WorldLockTargetItem {
  jointId: number;
  jointName: string;
  bodyId: number;
  bodyName: string;
  pos: number[];
  mat: number[];
}

interface WorldLockTarget {
  targets: WorldLockTargetItem[];
  jointId: number;
  jointName: string;
  lockPos: boolean;
  lockRot: boolean;
  lockedJointIds?: number[];
  fixedBaseRpyAxes?: number[];
}

type Variable =
  | { kind: 'base_pos'; axis: number }
  | { kind: 'base_rpy'; axis: number }
  | { kind: 'joint'; jointId: number };

interface WaistAxisConfig {
  axis: 'roll' | 'pitch';
  axisIdx: number;
  targetRoll: number | null;
  targetPitch: number | null;
  label: string;
}

export interface ZmpDetail {
  frame_idx: number;
  level: string;
  direction: string;
  zmp_margin: number;
  com_margin: number;
  zmp: number[];
  com: number[];
  reasons: string[];
}

export interface ZmpResult {
  score: number;
  high_risk: number[];
  warning_risk: number[];
  by_direction: Record<string, number[]>;
  details: ZmpDetail[];
  worst_text: string;
  summary: string;
  report: string;
}

export interface InterpolationOptions {
  startFrame: number;
  endFrame: number;
  interpBasePos?: boolean;
  interpBaseHeight?: boolean;
  interpBaseQuat?: boolean;
  interpJoints?: boolean;
  interpFeetLevel?: boolean;
  keyframes1based?: number[];
  selectedJointIndices?: number[];
}

export class RobotTrajectoryEditor {
  readonly model: MjModel;
  readonly data: MjData;
  readonly M: MujocoModule;

  freeJointId = -1;
  freeJointQposAddr = -1;

  jointInfos: JointInfo[] = [];
  trajectoryJointInfos: JointInfo[] = [];

  trajectoryData: number[][] | null = null;
  trajectoryIncludesBase = false;
  trajectoryBaseFormat: 'none' | 'euler' | 'quaternion' = 'none';
  trajectoryQuatFormat: 'wxyz' | 'xyzw' = 'wxyz';

  footLevelingMap: Record<string, FootSpec> = {};
  waistLevelingMap: WaistLevelingMap = {
    waistPitchJointId: null,
    waistRollJointId: null,
    torsoBodyId: -1,
    hasWaist: false,
  };

  // ---- mujoco live-view / 名字缓存 ----
  // 反复访问 data.qpos/xpos/xmat/xquat/xanchor 与 mj_id2name 会在 emscripten
  // 层反复创建临时包装对象（emval / std::string），高频交错调用（如锁踝调腰 IK
  // 逐帧循环）会耗尽并破坏 wasm 堆，最终在任意 mujoco 调用（如 mj_id2name 的
  // operator new）中抛 "RuntimeError: memory access out of bounds"。
  // 这些 view 是 mujoco 的 live view（mj_forward 后内容自动更新），只需缓存一次。
  private _qpos: Float64Array = new Float64Array(0);
  private _xpos: Float64Array = new Float64Array(0);
  private _xmat: Float64Array = new Float64Array(0);
  private _xquat: Float64Array = new Float64Array(0);
  private _xanchor: Float64Array = new Float64Array(0);
  private _xipos: Float64Array = new Float64Array(0);
  private _jointNameById: Record<number, string> = {};
  private _bodyNameById: Record<number, string> = {};

  constructor(handle: MjHandle) {
    this.model = handle.model;
    this.data = handle.data;
    this.M = handle.M;
    this._initMujocoCaches();
    this._createJointInfos();
  }

  /** 缓存 mujoco 的 live view 与名字映射，避免高频 embind getter / mj_id2name 调用。 */
  private _initMujocoCaches(): void {
    if (!this.model || !this.data || !this.M) return;
    try {
      this._qpos = this.data.qpos;
      this._xpos = this.data.xpos;
      this._xmat = this.data.xmat;
      this._xquat = this.data.xquat;
      this._xanchor = this.data.xanchor;
      this._xipos = this.data.xipos;
    } catch {
      // 视图不可用时不缓存，后续访问 this.data.* 兜底
    }
    this._jointNameById = {};
    this._bodyNameById = {};
    try {
      for (let i = 0; i < this.model.njnt; i++) {
        const nm = this.M.mj_id2name(this.model, MJOBJ_JOINT, i);
        if (nm) this._jointNameById[i] = String(nm);
      }
      for (let i = 0; i < this.model.nbody; i++) {
        const nm = this.M.mj_id2name(this.model, MJOBJ_BODY, i);
        if (nm) this._bodyNameById[i] = String(nm);
      }
    } catch {
      // 名字映射不可用时不缓存，后续 mj_id2name 兜底
    }
  }

  // ==================================================================
  // 关节信息构建（bridge_core._create_joint_infos）
  // ==================================================================

  private _createJointInfos(): void {
    this.jointInfos = [];
    this.freeJointId = -1;
    this.freeJointQposAddr = -1;
    if (!this.model) return;

    const joints: JointInfo[] = [];
    for (let i = 0; i < this.model.njnt; i++) {
      let jointName = this._jointNameById[i];
      if (jointName == null || jointName === '') jointName = `joint_${i}`;

      const jointType = this.model.jnt_type[i];

      if (jointType === MJJNT_FREE && this.freeJointId === -1) {
        this.freeJointId = i;
        this.freeJointQposAddr = this.model.jnt_qposadr[i];
        continue;
      }

      if (jointType === MJJNT_BALL) continue;

      let jointRange: [number, number] = [
        Number(this.model.jnt_range[i * 2]),
        Number(this.model.jnt_range[i * 2 + 1]),
      ];
      // 注意：不能直接访问 model.jnt_limited —— 该字段在 @mujoco/mujoco 3.10.0
      // 的 embind 绑定中是 memory_view<bool>，每次访问都会抛 BindingError
      // （_emval_take_value has unknown type memory_viewIbEE），异常反复穿过
      // wasm 边界会破坏引擎状态，最终在任意 mujoco 调用中报 memory access out
      // of bounds / table index is out of bounds。这里用 jnt_range 的相等性推断限位。
      const limited = jointRange[0] !== jointRange[1];
      if (!limited) {
        if (jointType === MJJNT_HINGE) jointRange = [-Math.PI, Math.PI];
        else jointRange = [-1.0, 1.0];
      }

      const qposAddr = Number(this.model.jnt_qposadr[i]);
      const initialValue =
        this.data && qposAddr >= 0 && qposAddr < this._qpos.length
          ? Number(this._qpos[qposAddr])
          : 0.0;

      joints.push({ jointId: i, jointName, jointRange, jointType, qposAddr, initialValue, limited });
    }

    joints.sort((a, b) => a.qposAddr - b.qposAddr);
    this.jointInfos = joints;
    this.trajectoryJointInfos = joints.slice();
  }

  // ==================================================================
  // 基座维度（bridge_core._get_base_dim）
  // ==================================================================

  private _getBaseDim(): number {
    if (this.trajectoryIncludesBase && this.freeJointId !== -1) {
      if (this.trajectoryBaseFormat === 'euler') return 6;
      if (this.trajectoryBaseFormat === 'quaternion') return 7;
    }
    return 0;
  }

  // ==================================================================
  // 帧应用 / 收集（bridge_core._apply_row_to_data_direct / _collect_row_from_data_like）
  // ==================================================================

  /** 把一行轨迹数据直接写入 MuJoCo qpos 并做 forward。 */
  private _applyRowToDataDirect(row: number[]): void {
    const baseOffset = this._getBaseDim();
    if (this.trajectoryIncludesBase && this.freeJointId !== -1 && baseOffset > 0) {
      const pos = [Number(row[0]), Number(row[1]), Number(row[2])];
      let quat: number[];
      if (this.trajectoryBaseFormat === 'euler') {
        quat = eulerToQuaternion([Number(row[3]), Number(row[4]), Number(row[5])]);
      } else {
        const qraw = [Number(row[3]), Number(row[4]), Number(row[5]), Number(row[6])];
        if (this.trajectoryQuatFormat === 'xyzw') {
          quat = [qraw[3], qraw[0], qraw[1], qraw[2]];
        } else {
          quat = qraw;
        }
        const qn = norm(quat);
        quat = qn > 1e-8 ? quat.map((x) => x / qn) : [1, 0, 0, 0];
      }
      const addr = this.freeJointQposAddr;
      this._qpos[addr] = pos[0];
      this._qpos[addr + 1] = pos[1];
      this._qpos[addr + 2] = pos[2];
      this._qpos[addr + 3] = quat[0];
      this._qpos[addr + 4] = quat[1];
      this._qpos[addr + 5] = quat[2];
      this._qpos[addr + 6] = quat[3];
    }

    for (let uiIdx = 0; uiIdx < this.trajectoryJointInfos.length; uiIdx++) {
      const info = this.trajectoryJointInfos[uiIdx];
      const dataIdx = baseOffset + uiIdx;
      if (dataIdx >= row.length) continue;
      const qposAddr = Number(this.model.jnt_qposadr[info.jointId]);
      if (qposAddr >= 0 && qposAddr < this._qpos.length) {
        this._qpos[qposAddr] = Number(row[dataIdx]);
      }
    }

    this.M.mj_forward(this.model, this.data);
  }

  /** 把当前 MuJoCo 状态收集回一行轨迹数据（保持与输入相同的列布局）。 */
  private _collectRowFromDataLike(row: number[]): number[] {
    const out = row.slice();
    const baseDim = this._getBaseDim();
    if (this.trajectoryIncludesBase && this.freeJointId !== -1 && baseDim > 0) {
      const addr = this.freeJointQposAddr;
      const pos = [
        Number(this._qpos[addr]),
        Number(this._qpos[addr + 1]),
        Number(this._qpos[addr + 2]),
      ];
      let quat = [
        Number(this._qpos[addr + 3]),
        Number(this._qpos[addr + 4]),
        Number(this._qpos[addr + 5]),
        Number(this._qpos[addr + 6]),
      ];
      const qn = norm(quat);
      quat = Number.isFinite(qn) && qn > 1e-10 ? quat.map((x) => x / qn) : [1, 0, 0, 0];

      out[0] = pos[0];
      out[1] = pos[1];
      out[2] = pos[2];
      if (this.trajectoryBaseFormat === 'euler' && out.length >= 6) {
        const rpy = quaternionToEuler(quat);
        out[3] = rpy[0];
        out[4] = rpy[1];
        out[5] = rpy[2];
      } else if (this.trajectoryBaseFormat === 'quaternion' && out.length >= 7) {
        if (this.trajectoryQuatFormat === 'xyzw') {
          out[3] = quat[1];
          out[4] = quat[2];
          out[5] = quat[3];
          out[6] = quat[0];
        } else {
          out[3] = quat[0];
          out[4] = quat[1];
          out[5] = quat[2];
          out[6] = quat[3];
        }
      }
    }

    for (let uiIdx = 0; uiIdx < this.trajectoryJointInfos.length; uiIdx++) {
      const info = this.trajectoryJointInfos[uiIdx];
      const col = baseDim + uiIdx;
      if (col >= out.length) continue;
      const qposAddr = Number(this.model.jnt_qposadr[info.jointId]);
      if (qposAddr >= 0 && qposAddr < this._qpos.length) {
        out[col] = Number(this._qpos[qposAddr]);
      }
    }
    return out;
  }

  private _getFrameDisplayRow(index: number): number[] | null {
    if (!this.trajectoryData) return null;
    const i = Math.trunc(index);
    if (i < 0 || i >= this.trajectoryData.length) return null;
    return this.trajectoryData[i];
  }

  // ==================================================================
  // 轨迹矩阵加载 / 回写（bridge_core.load_from_array / dump_to_array）
  // ==================================================================

  /**
   * 从 dict-list（JSON 数组）加载轨迹。
   * 期望每个 dict 的 key 与 CSV 表头顺序一致：root_pos_x/y/z, root_rot_x/y/z/w, 关节名...
   */
  loadFromArray(trajectoryData: Record<string, unknown>[]): void {
    console.log('[robotEdit] loadFromArray start', { frames: trajectoryData.length });
    try {
      if (!this.model) throw new Error('Please load a MuJoCo model first!');
      if (!trajectoryData || trajectoryData.length === 0) throw new Error('trajectory_data is empty');

      this.trajectoryData = null;
      this.trajectoryJointInfos = this.jointInfos.slice();
      this.trajectoryIncludesBase = false;
      this.trajectoryBaseFormat = 'none';
      this.trajectoryQuatFormat = 'wxyz';

      const firstRow = trajectoryData[0];
      const headerTokens = Object.keys(firstRow);

      const jointNameToInfo: Record<string, JointInfo> = {};
      for (const info of this.jointInfos) jointNameToInfo[info.jointName] = info;

      const baseNames = new Set([
        'root_pos_x', 'root_pos_y', 'root_pos_z',
        'root_rot_x', 'root_rot_y', 'root_rot_z', 'root_rot_w',
      ]);
      const nonbase = headerTokens.filter((h) => h && !baseNames.has(h));
      const missing = nonbase.filter((h) => !jointNameToInfo[h]);
      if (missing.length > 0) throw new Error(`Unknown joints in trajectory_data: ${missing}`);

      const trajInfos: JointInfo[] = [];
      for (const h of headerTokens) {
        if (h && !baseNames.has(h)) trajInfos.push(jointNameToInfo[h]);
      }

      const data: number[][] = trajectoryData.map((row) =>
        headerTokens.map((h) => {
          const v = row[h];
          const n = v == null ? 0 : Number(v);
          return Number.isFinite(n) ? n : 0;
        }),
      );

      this.trajectoryData = data;
      this.trajectoryJointInfos = trajInfos;

      const hasQuatBase = ['root_rot_x', 'root_rot_y', 'root_rot_z', 'root_rot_w'].every((x) =>
        headerTokens.includes(x),
      );
      const hasEulerBase = ['root_pos_x', 'root_pos_y', 'root_pos_z', 'root_rot_x', 'root_rot_y', 'root_rot_z'].every((x) =>
        headerTokens.includes(x),
      );
      if (hasQuatBase) {
        this.trajectoryIncludesBase = true;
        this.trajectoryBaseFormat = 'quaternion';
        this.trajectoryQuatFormat = 'xyzw';
      } else if (hasEulerBase) {
        this.trajectoryIncludesBase = true;
        this.trajectoryBaseFormat = 'euler';
      } else {
        this.trajectoryIncludesBase = headerTokens.some((h) => baseNames.has(h));
      }

      if (this.trajectoryData.length > 0) this._applyRowToDataDirect(this.trajectoryData[0]);
      console.log(
        `[robotEdit] Array loaded: ${data.length} frames, base=${this.trajectoryBaseFormat}`,
      );
    } finally {
      console.log('[robotEdit] loadFromArray end');
    }
  }

  /** 把轨迹矩阵回写为 dict-list（JSON 数组）。 */
  dumpToArray(): Record<string, number>[] {
    console.log('[robotEdit] dumpToArray start');
    try {
      if (!this.trajectoryData) return [];

      const headerTokens: string[] = [];
      if (this.trajectoryIncludesBase) {
        if (this.trajectoryBaseFormat === 'quaternion') {
          headerTokens.push('root_pos_x', 'root_pos_y', 'root_pos_z', 'root_rot_x', 'root_rot_y', 'root_rot_z', 'root_rot_w');
        } else if (this.trajectoryBaseFormat === 'euler') {
          headerTokens.push('root_pos_x', 'root_pos_y', 'root_pos_z', 'root_rot_x', 'root_rot_y', 'root_rot_z');
        }
      }
      for (const info of this.trajectoryJointInfos) headerTokens.push(info.jointName);

      return this.trajectoryData.map((row) => {
        const out: Record<string, number> = {};
        for (let j = 0; j < headerTokens.length; j++) {
          out[headerTokens[j]] = Number(row[j]);
        }
        return out;
      });
    } finally {
      console.log('[robotEdit] dumpToArray end');
    }
  }

  // ==================================================================
  // 脚部 / 腰部映射构建（foot_leveling._build_*）
  // ==================================================================

  private _buildFootLevelingMap(): void {
    this.footLevelingMap = {};
    if (!this.model) return;

    const leftPitch = this._findAnkleJointId('left', 'pitch');
    const leftRoll = this._findAnkleJointId('left', 'roll');
    const rightPitch = this._findAnkleJointId('right', 'pitch');
    const rightRoll = this._findAnkleJointId('right', 'roll');

    const leftBody = this._findBodyIdCandidates(
      ['contact_foot_center_l', 'left_foot_pitch_link', 'left_foot', 'left_ankle_pitch_link', 'left_ankle_roll_link', 'l_leg_ankle_roll_link', 'l_leg_ankle_pitch_link', 'l_ankle_roll_link', 'l_ankle_pitch_link'],
      'left',
    );
    const rightBody = this._findBodyIdCandidates(
      ['contact_foot_center_r', 'right_foot_pitch_link', 'right_foot', 'right_ankle_pitch_link', 'right_ankle_roll_link', 'r_leg_ankle_roll_link', 'r_leg_ankle_pitch_link', 'r_ankle_roll_link', 'r_ankle_pitch_link'],
      'right',
    );

    const [leftAxis, leftSign] = this._inferBodyUpAxis(leftBody);
    const [rightAxis, rightSign] = this._inferBodyUpAxis(rightBody);

    this.footLevelingMap['left'] = {
      side: 'left', pitchJointId: leftPitch, rollJointId: leftRoll,
      footBodyId: leftBody, upAxisIdx: leftAxis, upAxisSign: leftSign,
    };
    this.footLevelingMap['right'] = {
      side: 'right', pitchJointId: rightPitch, rollJointId: rightRoll,
      footBodyId: rightBody, upAxisIdx: rightAxis, upAxisSign: rightSign,
    };
  }

  private _buildWaistLevelingMap(): void {
    this.waistLevelingMap = { waistPitchJointId: null, waistRollJointId: null, torsoBodyId: -1, hasWaist: false };
    if (!this.model) return;

    const waistPitch = this._findWaistJoint('pitch');
    const waistRoll = this._findWaistJoint('roll');
    const torsoBodyId = this._findUpperBodyReferenceId();

    const waistPitchJid = waistPitch != null ? Number(waistPitch) : null;
    const waistRollJid = waistRoll != null ? Number(waistRoll) : null;
    const hasWaist = waistPitchJid != null || waistRollJid != null;

    this.waistLevelingMap = {
      waistPitchJointId: waistPitchJid,
      waistRollJointId: waistRollJid,
      torsoBodyId: Number(torsoBodyId),
      hasWaist,
    };
  }

  private _findWaistJoint(motion: string): number | null {
    if (!this.model) return null;
    for (const name of [`waist_${motion}_joint`, `waist_${motion}`]) {
      const jid = this._findJointIdByName(name);
      if (jid != null) return Number(jid);
    }
    for (const info of this.jointInfos) {
      const n = info.jointName.toLowerCase();
      if (n.includes('waist') && n.includes(motion)) return info.jointId;
    }
    return null;
  }

  private _nameHasSide(name: string, side: string): boolean {
    const n = String(name).toLowerCase();
    const tokens = n.split(/[^a-z0-9]+/).filter(Boolean);
    if (side === 'left') {
      if (tokens.includes('left') || tokens.includes('l') || tokens.includes('lf')) return true;
      return n.startsWith('l_') || n.includes('_l_') || n.endsWith('_l');
    }
    if (side === 'right') {
      if (tokens.includes('right') || tokens.includes('r') || tokens.includes('rf')) return true;
      return n.startsWith('r_') || n.includes('_r_') || n.endsWith('_r');
    }
    return false;
  }

  private _findAnkleJointId(side: string, motion: string): number | null {
    for (const info of this.jointInfos) {
      const n = info.jointName.toLowerCase();
      if (!n.includes('ankle')) continue;
      if (!n.includes(motion)) continue;
      if (this._nameHasSide(n, side)) return info.jointId;
    }
    return null;
  }

  private _findBodyIdCandidates(candidates: string[], side: string): number {
    let bestId = -1;
    let bestScore = -1e18;
    const bodyNameCache: Record<number, string> = {};
    for (let i = 0; i < this.model.nbody; i++) {
      const nm = this._bodyNameById[i];
      if (nm) bodyNameCache[i] = String(nm).toLowerCase();
    }

    for (const iStr of Object.keys(bodyNameCache)) {
      const i = Number(iStr);
      const n = bodyNameCache[i];
      if (side === 'left' || side === 'right') {
        if (!this._nameHasSide(n, side)) continue;
      }

      let score: number | null = null;
      for (let idx = 0; idx < candidates.length; idx++) {
        const cand = candidates[idx];
        if (n.includes(cand)) {
          score = 2000 - idx * 100 + cand.length;
          if (n === cand) score += 100;
          break;
        }
      }
      if (score === null) continue;

      if (n.includes('foot_pitch') || n.includes('ankle_pitch') || n.includes('sole') || n.includes('contact_foot_center')) {
        score += 120;
      }
      if (n.includes('foot_roll') || n.includes('ankle_roll')) score -= 60;

      let depth = 0;
      try {
        let cur = i;
        while (cur > 0) {
          cur = Number(this.model.body_parentid[cur]);
          depth += 1;
        }
      } catch {
        depth = 0;
      }
      score += 0.1 * depth;

      if (score > bestScore) {
        bestScore = score;
        bestId = i;
      }
    }

    if (bestId >= 0) return bestId;

    for (const iStr of Object.keys(bodyNameCache)) {
      const i = Number(iStr);
      const n = bodyNameCache[i];
      if (this._nameHasSide(n, side) && (n.includes('ankle_roll') || n.includes('foot_center'))) {
        return i;
      }
    }
    return -1;
  }

  private _inferBodyUpAxis(bodyId: number): [number, number] {
    if (bodyId < 0 || bodyId >= this.model.nbody) return [2, 1.0];
    try {
      const off = bodyId * 9;
      const row2 = [
        this._xmat[off + 6],
        this._xmat[off + 7],
        this._xmat[off + 8],
      ];
      const absRow = row2.map((v) => Math.abs(v));
      let axisIdx = 0;
      for (let k = 1; k < 3; k++) if (absRow[k] > absRow[axisIdx]) axisIdx = k;
      const axisSign = row2[axisIdx] >= 0 ? 1.0 : -1.0;
      return [axisIdx, axisSign];
    } catch {
      return [2, 1.0];
    }
  }

  private _findJointIdByName(exactName: string): number | null {
    if (!this.model) return null;
    for (let i = 0; i < this.model.njnt; i++) {
      const nm = this._jointNameById[i];
      if (nm === exactName) return Number(i);
    }
    return null;
  }

  private _findUpperBodyReferenceId(): number {
    if (!this.model) return -1;
    const candidates = ['torso_link', 'torso', 'chest', 'trunk', 'upper_body', 'waist_link', 'waist', 'pelvis_link', 'pelvis'];
    const bestId = this._findBodyIdCandidates(candidates, 'center');
    if (bestId >= 0) return bestId;

    for (let i = 0; i < this.model.nbody; i++) {
      const nm = this._bodyNameById[i];
      if (!nm) continue;
      const n = String(nm).toLowerCase();
      if (n.includes('torso') || n.includes('chest') || n.includes('trunk') || n.includes('upper')) return i;
    }
    return -1;
  }

  private _footSpecsForScope(scope: string): FootSpec[] {
    this._buildFootLevelingMap();
    const specs: FootSpec[] = [];
    const s = (scope || 'auto').toLowerCase();
    let sideKeys: string[];
    if (s === 'left' || s === '左脚') sideKeys = ['left'];
    else if (s === 'right' || s === '右脚') sideKeys = ['right'];
    else sideKeys = ['left', 'right'];

    for (const side of sideKeys) {
      const spec = { ...this.footLevelingMap[side] };
      spec.side = side;
      if ((spec.footBodyId ?? -1) >= 0) specs.push(spec);
    }
    return specs;
  }

  private _motionScore(
    frameIdx: number,
    side: string,
    framePositions: Record<number, Record<string, number[]>>,
  ): [number, number] {
    const cur = framePositions[frameIdx]?.[side];
    if (!cur) return [0, 0];
    let horiz = 0;
    let vert = 0;
    for (const nb of [frameIdx - 1, frameIdx + 1]) {
      const nbPos = framePositions[nb]?.[side];
      if (!nbPos) continue;
      const dh = norm3(cur[0] - nbPos[0], cur[1] - nbPos[1], 0);
      const dv = Math.abs(cur[2] - nbPos[2]);
      horiz = Math.max(horiz, dh);
      vert = Math.max(vert, dv);
    }
    return [horiz, vert];
  }

  private _findJointColumnById(jointId: number): number | null {
    const baseOffset = this._getBaseDim();
    for (let uiIdx = 0; uiIdx < this.trajectoryJointInfos.length; uiIdx++) {
      if (Number(this.trajectoryJointInfos[uiIdx].jointId) === Number(jointId)) {
        return baseOffset + uiIdx;
      }
    }
    return null;
  }

  // ==================================================================
  // 脚部调平 IK（ik_solver._get_body_upward_error / _get_body_tilt_error / _solve_one_foot_leveling）
  // ==================================================================

  private _getBodyUpwardError(bodyId: number, axisIdx = 2, axisSign = 1.0): number[] | null {
    if (bodyId < 0 || bodyId >= this.model.nbody) return null;
    const off = bodyId * 9;
    if (axisIdx !== 0 && axisIdx !== 1 && axisIdx !== 2) axisIdx = 2;
    const normal = [
      axisSign * this._xmat[off + axisIdx],
      axisSign * this._xmat[off + 3 + axisIdx],
      axisSign * this._xmat[off + 6 + axisIdx],
    ];
    const err = [normal[0], normal[1], normal[2] - 1.0];
    if (normal[2] < -0.95 && norm3(normal[0], normal[1], 0) < 1e-5) err[0] = 0.05;
    return err;
  }

  private _getBodyTiltError(bodyId: number, axisIdx = 2, axisSign = 1.0): number[] | null {
    const err3 = this._getBodyUpwardError(bodyId, axisIdx, axisSign);
    if (err3 === null) return null;
    return [err3[0], err3[1]];
  }

  private _solveOneFootLeveling(footSpec: FootSpec, maxIter = 20, tol = 1e-4): boolean {
    const pitchJointId = footSpec.pitchJointId;
    const rollJointId = footSpec.rollJointId;
    const footBodyId = footSpec.footBodyId;
    const upAxisIdx = footSpec.upAxisIdx ?? 2;
    const upAxisSign = footSpec.upAxisSign ?? 1.0;

    if (pitchJointId == null || rollJointId == null || footBodyId < 0) return false;

    const pitchQaddr = Number(this.model.jnt_qposadr[pitchJointId]);
    const rollQaddr = Number(this.model.jnt_qposadr[rollJointId]);
    if (pitchQaddr < 0 || rollQaddr < 0) return false;

    const jointVars = [rollJointId, pitchJointId];
    const qaddrs = [rollQaddr, pitchQaddr];
    const eps = 1e-4;
    const damping = 1e-3;
    const maxStep = 0.08;

    for (let iter = 0; iter < maxIter; iter++) {
      this.M.mj_forward(this.model, this.data);
      const err = this._getBodyTiltError(footBodyId, upAxisIdx, upAxisSign);
      if (err === null) return false;
      if (norm(err) < tol) return true;

      const J: number[][] = [[0, 0], [0, 0]];
      for (let j = 0; j < 2; j++) {
        const qaddr = qaddrs[j];
        const oldVal = Number(this._qpos[qaddr]);
        this._qpos[qaddr] = oldVal + eps;
        this.M.mj_forward(this.model, this.data);
        const errEps = this._getBodyTiltError(footBodyId, upAxisIdx, upAxisSign);
        this._qpos[qaddr] = oldVal;
        if (errEps === null) return false;
        J[0][j] = (errEps[0] - err[0]) / eps;
        J[1][j] = (errEps[1] - err[1]) / eps;
      }

      const jt = [[J[0][0], J[1][0]], [J[0][1], J[1][1]]];
      const a = [
        [jt[0][0] * J[0][0] + jt[0][1] * J[1][0] + damping, jt[0][0] * J[0][1] + jt[0][1] * J[1][1]],
        [jt[1][0] * J[0][0] + jt[1][1] * J[1][0], jt[1][0] * J[0][1] + jt[1][1] * J[1][1] + damping],
      ];
      const b = [
        -(jt[0][0] * err[0] + jt[0][1] * err[1]),
        -(jt[1][0] * err[0] + jt[1][1] * err[1]),
      ];
      const dq = solveLinearSystem(a, b);
      if (dq === null) return false;
      dq[0] = clip(dq[0], -maxStep, maxStep);
      dq[1] = clip(dq[1], -maxStep, maxStep);

      let movedAny = false;
      for (let j = 0; j < 2; j++) {
        const jid = jointVars[j];
        const oldQ = Number(this._qpos[qaddrs[j]]);
        let newQ = oldQ + dq[j];
        // 不能访问 model.jnt_limited（embind bool view 会抛 BindingError），用 jointInfos 的限位信息
        const jinfo = this.jointInfos.find((x) => x.jointId === jid);
        if (jinfo && jinfo.limited) {
          newQ = clip(newQ, jinfo.jointRange[0], jinfo.jointRange[1]);
        }
        this._qpos[qaddrs[j]] = newQ;
        if (Math.abs(newQ - oldQ) > 1e-10) movedAny = true;
      }
      if (!movedAny) break;
    }

    this.M.mj_forward(this.model, this.data);
    const finalErr = this._getBodyTiltError(footBodyId, upAxisIdx, upAxisSign);
    return finalErr !== null && norm(finalErr) < 5.0 * tol;
  }

  private _setDataBaseRollPitch(targetRoll: number | null, targetPitch: number | null): boolean {
    if (!this.data || this.freeJointQposAddr < 0) return false;
    const baseAddr = this.freeJointQposAddr;
    if (baseAddr + 7 > this._qpos.length) return false;

    let quat = [
      Number(this._qpos[baseAddr + 3]),
      Number(this._qpos[baseAddr + 4]),
      Number(this._qpos[baseAddr + 5]),
      Number(this._qpos[baseAddr + 6]),
    ];
    const qn = norm(quat);
    if (!Number.isFinite(qn) || qn < 1e-10) quat = [1, 0, 0, 0];
    else quat = quat.map((x) => x / qn);

    const rpy = quaternionToEuler(quat);
    if (targetRoll !== null) rpy[0] = targetRoll;
    if (targetPitch !== null) rpy[1] = targetPitch;
    let quatNew = eulerToQuaternion(rpy);
    if (dot(quatNew, quat) < 0) quatNew = quatNew.map((x) => -x);

    this._qpos[baseAddr + 3] = quatNew[0];
    this._qpos[baseAddr + 4] = quatNew[1];
    this._qpos[baseAddr + 5] = quatNew[2];
    this._qpos[baseAddr + 6] = quatNew[3];
    this.M.mj_forward(this.model, this.data);
    return true;
  }

  // ==================================================================
  // 位姿访问工具
  // ==================================================================

  private _getXpos(bodyId: number): number[] {
    const off = bodyId * 3;
    return [this._xpos[off], this._xpos[off + 1], this._xpos[off + 2]];
  }

  private _getXmat(bodyId: number): number[] {
    const off = bodyId * 9;
    const m = this._xmat;
    return [m[off], m[off + 1], m[off + 2], m[off + 3], m[off + 4], m[off + 5], m[off + 6], m[off + 7], m[off + 8]];
  }

  private _getXquat(bodyId: number): number[] {
    const off = bodyId * 4;
    const m = this._xquat;
    return [m[off], m[off + 1], m[off + 2], m[off + 3]];
  }

  // ==================================================================
  // 世界位姿锁定 IK（world_lock_ik）
  // ==================================================================

  private _jointWorldLockPose(jointId: number): { pos: number[]; mat: number[]; bodyId: number } | null {
    if (!this.model || !this.data) return null;
    const jid = Math.trunc(jointId);
    if (jid < 0 || jid >= this.model.njnt) return null;
    const bodyId = Number(this.model.jnt_bodyid[jid]);
    if (bodyId < 0 || bodyId >= this.model.nbody) return null;
    this.M.mj_forward(this.model, this.data);

    let pos: number[];
    try {
      const off = jid * 3;
      pos = [this._xanchor[off], this._xanchor[off + 1], this._xanchor[off + 2]];
    } catch {
      pos = this._getXpos(bodyId);
    }
    const mat = this._getXmat(bodyId);
    return { pos, mat, bodyId };
  }

  private _makeWorldLockTargetForJointIds(
    jointIds: number[],
    lockPos = true,
    lockRot = true,
  ): WorldLockTarget | null {
    const targets: WorldLockTargetItem[] = [];
    for (const rawJid of jointIds) {
      const jid = Math.trunc(rawJid);
      const pose = this._jointWorldLockPose(jid);
      if (pose === null) continue;
      const jointName = this._jointNameById[jid] || `joint_${jid}`;
      const bodyName = this._bodyNameById[pose.bodyId] || `body_${pose.bodyId}`;
      targets.push({
        jointId: jid,
        jointName,
        bodyId: pose.bodyId,
        bodyName,
        pos: pose.pos,
        mat: pose.mat,
      });
    }
    if (targets.length === 0) return null;
    return {
      targets,
      jointId: targets[0].jointId,
      jointName: targets[0].jointName,
      lockPos: Boolean(lockPos),
      lockRot: Boolean(lockRot),
    };
  }

  /** 计算 a @ b.T（3x3 矩阵乘转置），a、b 均为 3x3 行主序一维数组。 */
  private _matMulTranspose(a: number[], b: number[]): number[] {
    const out = new Array(9);
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        let s = 0;
        for (let k = 0; k < 3; k++) s += a[r * 3 + k] * b[c * 3 + k];
        out[r * 3 + c] = s;
      }
    }
    return out;
  }

  private _worldLockResidual(target: WorldLockTarget): number[] | null {
    const parts: number[] = [];
    let targets = target.targets;
    if (targets == null || targets.length === 0) targets = [target as unknown as WorldLockTargetItem];
    for (const item of targets) {
      const pose = this._jointWorldLockPose(Math.trunc(item.jointId));
      if (pose === null) return null;
      const pos = pose.pos;
      const mat = pose.mat;
      if (target.lockPos) {
        parts.push(pos[0] - item.pos[0], pos[1] - item.pos[1], pos[2] - item.pos[2]);
      }
      if (target.lockRot) {
        const rotErr = this._matMulTranspose(mat, item.mat);
        const rv = rotationMatrixToRotvec(rotErr);
        parts.push(rv[0], rv[1], rv[2]);
      }
    }
    return parts;
  }

  private _buildWorldLockVariables(lockedJointIds: number[], includeBaseRpyAxes: number[]): Variable[] {
    const variables: Variable[] = [];
    if (!this.model || !this.data) return variables;
    const lockedSet = new Set(lockedJointIds.map((j) => Math.trunc(j)));
    const includeAxes = (includeBaseRpyAxes ?? [0, 1, 2]).filter((a) => a === 0 || a === 1 || a === 2);

    if (this.freeJointId !== -1 && this.freeJointQposAddr >= 0) {
      for (let axis = 0; axis < 3; axis++) variables.push({ kind: 'base_pos', axis });
      for (const axis of includeAxes) variables.push({ kind: 'base_rpy', axis });
    }

    for (const info of this.trajectoryJointInfos) {
      const jid = Math.trunc(info.jointId);
      if (lockedSet.has(jid) || jid === this.freeJointId) continue;
      if (jid < 0 || jid >= this.model.njnt) continue;
      const jt = Number(this.model.jnt_type[jid]);
      if (jt !== MJJNT_HINGE && jt !== MJJNT_SLIDE) continue;
      const qposAddr = Number(this.model.jnt_qposadr[jid]);
      if (qposAddr < 0 || qposAddr >= this._qpos.length) continue;
      variables.push({ kind: 'joint', jointId: jid });
    }
    return variables;
  }

  private _readBaseQuatWxyz(): number[] {
    const addr = this.freeJointQposAddr;
    let quat = [
      Number(this._qpos[addr + 3]),
      Number(this._qpos[addr + 4]),
      Number(this._qpos[addr + 5]),
      Number(this._qpos[addr + 6]),
    ];
    const qn = norm(quat);
    if (!Number.isFinite(qn) || qn < 1e-10) quat = [1, 0, 0, 0];
    else quat = quat.map((x) => x / qn);
    return quat;
  }

  private _writeBaseQuatWxyz(quat: number[]): void {
    const addr = this.freeJointQposAddr;
    this._qpos[addr + 3] = quat[0];
    this._qpos[addr + 4] = quat[1];
    this._qpos[addr + 5] = quat[2];
    this._qpos[addr + 6] = quat[3];
  }

  private _getWorldLockVariableValue(v: Variable): number {
    if (v.kind === 'base_pos') return Number(this._qpos[this.freeJointQposAddr + v.axis]);
    if (v.kind === 'base_rpy') {
      const quat = this._readBaseQuatWxyz();
      return quaternionToEuler(quat)[v.axis];
    }
    return Number(this._qpos[this.model.jnt_qposadr[v.jointId]]);
  }

  private _jointLimitForWorldLock(jointId: number): [number, number] {
    const jid = Math.trunc(jointId);
    for (const info of this.jointInfos) {
      if (info.jointId === jid) {
        // jointInfos.jointRange 对无限制关节已展开为 ±π / ±1
        return [info.jointRange[0], info.jointRange[1]];
      }
    }
    return [-Infinity, Infinity];
  }

  private _setWorldLockVariableValue(v: Variable, value: number): void {
    if (v.kind === 'base_pos') {
      this._qpos[this.freeJointQposAddr + v.axis] = value;
      return;
    }
    if (v.kind === 'base_rpy') {
      const quat = this._readBaseQuatWxyz();
      const rpy = quaternionToEuler(quat);
      rpy[v.axis] = value;
      let quatNew = eulerToQuaternion(rpy);
      if (dot(quatNew, quat) < 0) quatNew = quatNew.map((x) => -x);
      this._writeBaseQuatWxyz(quatNew);
      return;
    }
    const [low, high] = this._jointLimitForWorldLock(v.jointId);
    this._qpos[this.model.jnt_qposadr[v.jointId]] = clip(value, low, high);
  }

  private _applyWorldLockDelta(variables: Variable[], delta: number[], maxPosStep = 0.08, maxAngleStep = 0.12): void {
    for (let i = 0; i < variables.length; i++) {
      const v = variables[i];
      let step = Number(delta[i]);
      step = v.kind === 'base_pos' ? clip(step, -maxPosStep, maxPosStep) : clip(step, -maxAngleStep, maxAngleStep);
      const old = this._getWorldLockVariableValue(v);
      this._setWorldLockVariableValue(v, old + step);
    }
  }

  private _worldLockVariableCosts(variables: Variable[]): number[] {
    return variables.map((v) => (v.kind.startsWith('base_') ? 6.0 : 1.0));
  }

  private _solveWorldLockIk(
    target: WorldLockTarget,
    maxIter = 35,
    tol = 1e-4,
    damping = 1e-4,
  ): [boolean, number, number] {
    const targets = target.targets;
    let lockedJointIds: number[];
    if (target.lockedJointIds != null) lockedJointIds = target.lockedJointIds.map((j) => Math.trunc(j));
    else if (targets == null || targets.length === 0) lockedJointIds = [Math.trunc(target.jointId)];
    else lockedJointIds = targets.map((t) => Math.trunc(t.jointId));

    const fixedBaseRpyAxes = new Set((target.fixedBaseRpyAxes ?? []).map((a) => Math.trunc(a)));
    const includeBaseRpyAxes = [0, 1, 2].filter((a) => !fixedBaseRpyAxes.has(a));
    const variables = this._buildWorldLockVariables(lockedJointIds, includeBaseRpyAxes);
    if (variables.length === 0) return [false, Infinity, 0];

    const variableCosts = this._worldLockVariableCosts(variables);
    const invCost = variableCosts.map((c) => 1 / Math.pow(Math.max(c, 1e-6), 2));

    const fixedRpyValues: Record<number, number> = {};
    if (fixedBaseRpyAxes.size > 0 && this.freeJointQposAddr >= 0) {
      const quat = this._readBaseQuatWxyz();
      const rpy = quaternionToEuler(quat);
      for (const axis of fixedBaseRpyAxes) fixedRpyValues[axis] = rpy[axis];
    }

    const restoreFixedRpy = (): void => {
      if (Object.keys(fixedRpyValues).length > 0 && this.freeJointQposAddr >= 0) {
        const quat = this._readBaseQuatWxyz();
        const rpy = quaternionToEuler(quat);
        for (const key of Object.keys(fixedRpyValues)) rpy[Number(key)] = fixedRpyValues[Number(key)];
        let quatNew = eulerToQuaternion(rpy);
        if (dot(quatNew, quat) < 0) quatNew = quatNew.map((x) => -x);
        this._writeBaseQuatWxyz(quatNew);
        this.M.mj_forward(this.model, this.data);
      }
    };

    let bestNorm = Infinity;
    let solvedIter = 0;

    for (let it = 0; it < maxIter; it++) {
      restoreFixedRpy();
      this.M.mj_forward(this.model, this.data);
      const err = this._worldLockResidual(target);
      if (err === null) return [false, Infinity, it];
      if (err.length === 0) return [true, 0, it];
      const errNorm = norm(err);
      bestNorm = Math.min(bestNorm, errNorm);
      solvedIter = it + 1;
      if (errNorm < tol) return [true, errNorm, solvedIter];

      const qStart = this._qpos.slice();
      const nVar = variables.length;
      const jac: number[][] = Array.from({ length: err.length }, () => new Array(nVar).fill(0));
      const eps = 1e-4;

      for (let vi = 0; vi < nVar; vi++) {
        const v = variables[vi];
        const old = this._getWorldLockVariableValue(v);
        const step = v.kind === 'base_pos' ? eps : 5e-5;
        this._setWorldLockVariableValue(v, old + step);
        this.M.mj_forward(this.model, this.data);
        const errEps = this._worldLockResidual(target);
        if (errEps !== null && errEps.length === err.length) {
          for (let r = 0; r < err.length; r++) jac[r][vi] = (errEps[r] - err[r]) / step;
        }
        this._qpos.set(qStart);
        this.M.mj_forward(this.model, this.data);
      }

      const M = err.length;
      const lhs: number[][] = Array.from({ length: M }, () => new Array(M).fill(0));
      for (let r = 0; r < M; r++) {
        for (let c = 0; c < M; c++) {
          let s = 0;
          for (let k = 0; k < nVar; k++) s += jac[r][k] * jac[c][k] * invCost[k];
          lhs[r][c] = s + (r === c ? damping : 0);
        }
      }
      const sol = solveLinearSystem(lhs, err);
      if (sol === null) return [false, errNorm, solvedIter];

      const delta = new Array(nVar).fill(0);
      for (let k = 0; k < nVar; k++) {
        let s = 0;
        for (let r = 0; r < M; r++) s += jac[r][k] * invCost[k] * sol[r];
        delta[k] = -s;
      }

      let accepted = false;
      for (const scale of [1.0, 0.5, 0.25, 0.1]) {
        this._qpos.set(qStart);
        this._applyWorldLockDelta(variables, delta.map((d) => d * scale));
        this.M.mj_forward(this.model, this.data);
        const newErr = this._worldLockResidual(target);
        if (newErr === null) continue;
        const newNorm = norm(newErr);
        if (newNorm < errNorm) {
          accepted = true;
          bestNorm = Math.min(bestNorm, newNorm);
          break;
        }
      }
      if (!accepted) {
        this._qpos.set(qStart);
        this.M.mj_forward(this.model, this.data);
        break;
      }
    }

    restoreFixedRpy();
    const finalErr = this._worldLockResidual(target);
    const finalNorm = finalErr !== null ? norm(finalErr) : bestNorm;
    return [finalNorm < 5.0 * tol, finalNorm, solvedIter];
  }

  // ==================================================================
  // 双脚调平（foot_leveling.foot_leveling）
  // ==================================================================

  footLeveling(startFrame: number, endFrame: number): void {
    console.log('[robotEdit] footLeveling start', { startFrame, endFrame });
    try {
      if (!this.trajectoryData || !this.model || !this.data) throw new Error('请先加载模型与轨迹！');

      const startIdx = Math.max(0, Math.trunc(startFrame));
      const endIdx = Math.min(Math.trunc(endFrame), this.trajectoryData.length - 1);
      if (startIdx >= endIdx) throw new Error('请检查起止帧范围（至少3帧，起始<结束）。');

      this._buildFootLevelingMap();
      const left = this.footLevelingMap['left'];
      const right = this.footLevelingMap['right'];

      const missing: string[] = [];
      for (const [sideName, spec] of [['左脚', left], ['右脚', right]] as [string, FootSpec][]) {
        if (spec.pitchJointId == null) missing.push(`${sideName}踝关节pitch`);
        if (spec.rollJointId == null) missing.push(`${sideName}踝关节roll`);
        if ((spec.footBodyId ?? -1) < 0) missing.push(`${sideName}脚底参考体`);
      }
      if (missing.length > 0) throw new Error('模型中未找到以下必要对象:\n' + missing.join('\n'));

      const baseOffset = this._getBaseDim();
      const jointColById: Record<number, number> = {};
      for (let uiIdx = 0; uiIdx < this.trajectoryJointInfos.length; uiIdx++) {
        jointColById[Number(this.trajectoryJointInfos[uiIdx].jointId)] = baseOffset + uiIdx;
      }

      let editedCount = 0;
      let failCount = 0;
      for (let frameIdx = startIdx; frameIdx <= endIdx; frameIdx++) {
        const row = this.trajectoryData[frameIdx].slice();
        this._applyRowToDataDirect(row);

        const okL = this._solveOneFootLeveling(left);
        const okR = this._solveOneFootLeveling(right);
        if (!okL || !okR) failCount++;

        for (const spec of [left, right]) {
          for (const jidKey of ['pitchJointId', 'rollJointId'] as const) {
            const jid = spec[jidKey];
            if (jid == null) continue;
            const col = jointColById[Number(jid)];
            if (col == null || col >= row.length) continue;
            const qaddr = Number(this.model.jnt_qposadr[Number(jid)]);
            if (qaddr >= 0 && qaddr < this._qpos.length) row[col] = Number(this._qpos[qaddr]);
          }
        }
        this.trajectoryData[frameIdx] = row;
        editedCount++;
      }

      if (failCount === 0) {
        console.log(`[robotEdit] 调平完成: 区间 ${startIdx + 1}-${endIdx + 1}, 处理 ${editedCount} 帧`);
      } else {
        console.log(`[robotEdit] 调平完成: 处理 ${editedCount} 帧, ${failCount} 帧未完全收敛`);
      }
    } finally {
      console.log('[robotEdit] footLeveling end');
    }
  }

  // ==================================================================
  // 智能调脚（foot_leveling.smart_foot_adjust）
  // ==================================================================

  smartFootAdjust(startFrame: number, endFrame: number, scope = 'auto'): void {
    console.log('[robotEdit] smartFootAdjust start', { startFrame, endFrame, scope });
    try {
      if (!this.trajectoryData || !this.model || !this.data) throw new Error('请先加载模型与轨迹！');

      const feet = this._footSpecsForScope(scope);
      if (feet.length === 0) throw new Error('未找到脚底参考体，请检查 robot xml 的脚部命名。');

      const startIdx = Math.max(0, Math.trunc(startFrame));
      const endIdx = Math.min(Math.trunc(endFrame), this.trajectoryData.length - 1);
      if (startIdx > endIdx) return;

      const framePositions: Record<number, Record<string, number[]>> = {};
      const allZ: number[] = [];
      for (let frameIdx = startIdx; frameIdx <= endIdx; frameIdx++) {
        const row = this.trajectoryData[frameIdx].slice();
        this._applyRowToDataDirect(row);
        const sidePos: Record<string, number[]> = {};
        for (const spec of feet) {
          const bodyId = spec.footBodyId;
          if (bodyId < 0) continue;
          const pos = this._getXpos(bodyId).slice();
          sidePos[spec.side] = pos;
          allZ.push(pos[2]);
        }
        framePositions[frameIdx] = sidePos;
      }

      if (allZ.length === 0) return;

      let floorZ = quantile(allZ, 0.05);
      if (floorZ > 0.035 || floorZ < -0.035) floorZ = 0.0;

      let ankleLevelCount = 0;
      let intentionalLiftCount = 0;
      let fallbackSupportCount = 0;
      let failCount = 0;

      for (let frameIdx = startIdx; frameIdx <= endIdx; frameIdx++) {
        const row = this.trajectoryData[frameIdx].slice();
        const sidePos = framePositions[frameIdx] ?? {};
        if (Object.keys(sidePos).length === 0) continue;

        let contactSpecs: FootSpec[] = [];
        const liftSpecs: FootSpec[] = [];
        let lowestZ = floorZ;
        for (const p of Object.values(sidePos)) lowestZ = Math.min(lowestZ, p[2]);

        for (const spec of feet) {
          const pos = sidePos[spec.side];
          if (!pos) continue;
          const relZ = pos[2] - floorZ;
          const heightGap = pos[2] - lowestZ;
          const [horiz, vert] = this._motionScore(frameIdx, spec.side, framePositions);
          const realLift =
            (relZ > 0.025 && (horiz > 0.006 || vert > 0.0025)) ||
            (relZ > 0.045 && heightGap > 0.025) ||
            (relZ > 0.03 && heightGap > 0.018 && horiz > 0.003);
          if (realLift) liftSpecs.push(spec);
          else contactSpecs.push(spec);
        }

        intentionalLiftCount += liftSpecs.length;

        if (contactSpecs.length === 0 && liftSpecs.length > 0) continue;

        if (contactSpecs.length === 0) {
          let lowest = feet[0];
          for (const sp of feet) {
            const zCur = sidePos[sp.side]?.[2] ?? Infinity;
            const zLow = sidePos[lowest.side]?.[2] ?? Infinity;
            if (zCur <= zLow) lowest = sp;
          }
          contactSpecs = [lowest];
          fallbackSupportCount++;
        }

        this._applyRowToDataDirect(row);
        for (const spec of contactSpecs) {
          const ok = this._solveOneFootLeveling(spec);
          if (ok) ankleLevelCount++;
          else failCount++;
          for (const jidKey of ['pitchJointId', 'rollJointId'] as const) {
            const jid = spec[jidKey];
            if (jid == null) continue;
            const col = this._findJointColumnById(Number(jid));
            if (col == null || col >= row.length) continue;
            const qaddr = Number(this.model.jnt_qposadr[Number(jid)]);
            if (qaddr >= 0 && qaddr < this._qpos.length) row[col] = Number(this._qpos[qaddr]);
          }
        }
        this.trajectoryData[frameIdx] = row;
      }

      let msg = `智能调脚完成: 区间 ${startIdx + 1}-${endIdx + 1}，踝部调平 ${ankleLevelCount} 次，识别并保留真实抬脚 ${intentionalLiftCount} 次`;
      if (failCount) msg += `，${failCount} 次踝部未完全收敛`;
      if (fallbackSupportCount) msg += `，${fallbackSupportCount} 帧使用最低脚作支撑`;
      console.log('[robotEdit] ' + msg);
    } finally {
      console.log('[robotEdit] smartFootAdjust end');
    }
  }

  // ==================================================================
  // 锁踝调腰（ankle_locked_upright）
  // ==================================================================

  private _intervalWaistAxisConfig(axis: string): WaistAxisConfig {
    const a = String(axis || 'pitch').toLowerCase();
    if (a === 'roll') {
      return { axis: 'roll', axisIdx: 0, targetRoll: 0.0, targetPitch: null, label: 'Roll调腰' };
    }
    return { axis: 'pitch', axisIdx: 1, targetRoll: null, targetPitch: 0.0, label: 'Pitch调腰' };
  }

  private _ankleLockJointIds(): number[] {
    const jointIds: number[] = [];
    this._buildFootLevelingMap();
    for (const side of ['left', 'right']) {
      const spec = this.footLevelingMap[side];
      if (!spec) continue;
      for (const key of ['pitchJointId', 'rollJointId'] as const) {
        const jid = spec[key];
        if (jid != null) jointIds.push(Number(jid));
      }
    }
    if (jointIds.length === 0) {
      for (const info of this.jointInfos) {
        const name = info.jointName.toLowerCase();
        if (name.includes('ankle')) jointIds.push(info.jointId);
      }
    }
    return Array.from(new Set(jointIds)).sort((a, b) => a - b);
  }

  private _anklePoseLockJointIds(): number[] {
    const jointIds: number[] = [];
    this._buildFootLevelingMap();
    for (const side of ['left', 'right']) {
      const spec = this.footLevelingMap[side];
      if (!spec) continue;
      let jid = spec.rollJointId;
      if (jid == null) jid = spec.pitchJointId;
      if (jid != null) jointIds.push(Number(jid));
    }
    const dedup = Array.from(new Set(jointIds)).sort((a, b) => a - b);
    return dedup.length > 0 ? dedup : this._ankleLockJointIds();
  }

  private _setWaistJointTowardUpright(waistJid: number, axisCfg: WaistAxisConfig): void {
    if (!this.data || !this.model) return;
    const jid = Math.trunc(waistJid);
    if (jid < 0 || jid >= this.model.njnt) return;
    const qposAddr = Number(this.model.jnt_qposadr[jid]);
    if (qposAddr < 0 || qposAddr + 1 > this._qpos.length) return;

    const torsoBodyId = this.waistLevelingMap.torsoBodyId;
    if (torsoBodyId < 0 || torsoBodyId >= this.model.nbody) return;

    this.M.mj_forward(this.model, this.data);
    const quat = this._getXquat(torsoBodyId);
    const euler = quaternionToEuler(quat);
    const angle = euler[axisCfg.axisIdx];

    const currentVal = Number(this._qpos[qposAddr]);
    this._qpos[qposAddr] = currentVal - angle;
  }

  private _makeTorsoUprightConstraint(axisCfg: WaistAxisConfig): WorldLockTargetItem | null {
    if (!this.model || !this.data) return null;
    const torsoBodyId = this.waistLevelingMap.torsoBodyId;
    if (torsoBodyId < 0 || torsoBodyId >= this.model.nbody) return null;

    let torsoJointId = -1;
    for (let jid = 0; jid < this.model.njnt; jid++) {
      if (Number(this.model.jnt_bodyid[jid]) === torsoBodyId) {
        torsoJointId = jid;
        break;
      }
    }
    if (torsoJointId < 0) {
      for (let jid = 0; jid < this.model.njnt; jid++) {
        const bid = Number(this.model.jnt_bodyid[jid]);
        if (bid >= 0 && bid < this.model.nbody) {
          const parent = Number(this.model.body_parentid[bid]);
          if (parent === torsoBodyId) {
            torsoJointId = jid;
            break;
          }
        }
      }
    }

    this.M.mj_forward(this.model, this.data);
    const quat = this._getXquat(torsoBodyId);
    const euler = quaternionToEuler(quat);

    const targetEuler = euler.slice();
    if (axisCfg.axis === 'pitch') targetEuler[1] = 0.0;
    else targetEuler[0] = 0.0;
    const targetQuat = eulerToQuaternion(targetEuler);
    const [w, x, y, z] = targetQuat;
    const targetMat = [
      1 - 2 * (y * y + z * z), 2 * (x * y - w * z), 2 * (x * z + w * y),
      2 * (x * y + w * z), 1 - 2 * (x * x + z * z), 2 * (y * z - w * x),
      2 * (x * z - w * y), 2 * (y * z + w * x), 1 - 2 * (x * x + y * y),
    ];

    const pos = this._getXpos(torsoBodyId).slice();
    const jointName =
      torsoJointId >= 0
        ? this._jointNameById[torsoJointId] || `joint_${torsoJointId}`
        : 'joint_torso_virtual';
    const bodyName = this._bodyNameById[torsoBodyId] || `body_${torsoBodyId}`;

    return {
      jointId: Math.max(torsoJointId, 0),
      jointName,
      bodyId: torsoBodyId,
      bodyName,
      pos,
      mat: targetMat,
    };
  }

  ankleLockedBaseUpright(startFrame: number, endFrame: number, axis = 'pitch'): void {
    console.log('[robotEdit] ankleLockedBaseUpright start', { startFrame, endFrame, axis });
    try {
      if (!this.trajectoryData || !this.model || !this.data) throw new Error('请先加载模型与轨迹！');

      const axisCfg = this._intervalWaistAxisConfig(axis);
      if (
        !(
          this.freeJointId !== -1 &&
          this.freeJointQposAddr >= 0 &&
          this.trajectoryIncludesBase &&
          (this.trajectoryBaseFormat === 'euler' || this.trajectoryBaseFormat === 'quaternion')
        )
      ) {
        throw new Error('当前CSV没有可写入的基座位姿列，无法调整 base roll/pitch。');
      }

      const ankleJointIds = this._ankleLockJointIds();
      const anklePoseJointIds = this._anklePoseLockJointIds();
      if (ankleJointIds.length === 0) throw new Error('未找到 ankle pitch/roll 关节，无法建立锁定目标。');

      this._buildWaistLevelingMap();
      const hasWaist = this.waistLevelingMap.hasWaist;
      let waistJid: number | null = null;
      if (hasWaist) {
        waistJid = axisCfg.axis === 'pitch' ? this.waistLevelingMap.waistPitchJointId : this.waistLevelingMap.waistRollJointId;
      }

      const startIdx = Math.max(0, Math.trunc(startFrame));
      const endIdx = Math.min(Math.trunc(endFrame), this.trajectoryData.length - 1);
      if (startIdx > endIdx) return;

      let editedCount = 0;
      let failCount = 0;
      const residuals: number[] = [];

      for (let frameIdx = startIdx; frameIdx <= endIdx; frameIdx++) {
        const rowSrc = this._getFrameDisplayRow(frameIdx);
        if (rowSrc === null) {
          failCount++;
          continue;
        }
        const row = rowSrc.slice();
        this._applyRowToDataDirect(row);

        const target = this._makeWorldLockTargetForJointIds(anklePoseJointIds, true, true);
        if (target === null) {
          failCount++;
          continue;
        }

        if (hasWaist && waistJid != null) {
          this._setWaistJointTowardUpright(waistJid, axisCfg);
          target.fixedBaseRpyAxes = [0, 1, 2];
          target.lockedJointIds = ankleJointIds;
          const torsoConstraint = this._makeTorsoUprightConstraint(axisCfg);
          if (torsoConstraint != null) target.targets.push(torsoConstraint);
        } else {
          target.fixedBaseRpyAxes = axisCfg.axis === 'pitch' ? [0] : [1];
          target.lockedJointIds = ankleJointIds;
          this._setDataBaseRollPitch(axisCfg.targetRoll, axisCfg.targetPitch);
        }

        const [ok, residual] = this._solveWorldLockIk(target, 50, 2e-3, 1e-4);
        residuals.push(residual);
        if (!ok) failCount++;

        const newRow = this._collectRowFromDataLike(row);
        this.trajectoryData[frameIdx] = newRow;
        editedCount++;
      }

      const modeLabel = hasWaist && waistJid != null ? '腰部关节调整' : 'base直接调整';
      const maxResidual = residuals.length > 0 ? Math.max(...residuals) : 0.0;
      let msg =
        `${axisCfg.label}完成（${modeLabel}）: 第 ${startIdx + 1}-${endIdx + 1} 帧，处理 ${editedCount} 帧，` +
        `固定 ankle 角度 ${ankleJointIds.length} 个，锁定脚部世界位姿 ${anklePoseJointIds.length} 个，最大残差 ${maxResidual.toFixed(6)}`;
      if (failCount) msg += `，${failCount} 帧未完全收敛`;
      console.log('[robotEdit] ' + msg);
    } finally {
      console.log('[robotEdit] ankleLockedBaseUpright end');
    }
  }

  // ==================================================================
  // 关键帧插值（interpolation）
  // ==================================================================

  interpolation(options: InterpolationOptions): void {
    console.log('[robotEdit] interpolation start', options);
    try {
      const doInterp = Boolean(
        options.interpBasePos || options.interpBaseHeight || options.interpBaseQuat || options.interpJoints,
      );
      const doLevel = Boolean(options.interpFeetLevel);
      if (!doInterp && !doLevel) return;
      if (doInterp) this._doInterpolation(options);
      if (doLevel) this.footLeveling(options.startFrame, options.endFrame);
    } finally {
      console.log('[robotEdit] interpolation end');
    }
  }

  private _doInterpolation(o: InterpolationOptions): void {
    if (!this.trajectoryData || !this.model) throw new Error('请先加载模型与轨迹！');

    const startIdx = Math.max(0, Math.trunc(o.startFrame));
    const endIdx = Math.min(Math.trunc(o.endFrame), this.trajectoryData.length - 1);
    const maxIdx = this.trajectoryData.length - 1;

    if (startIdx < 0 || endIdx > maxIdx) throw new Error(`帧号必须在 1-${maxIdx + 1} 范围内`);
    if (startIdx >= endIdx) throw new Error('起始帧必须小于结束帧');

    const frameCount = endIdx - startIdx + 1;
    if (frameCount < 2) throw new Error('插值至少需要2帧（起始和结束）');

    const keySet = new Set<number>([startIdx, endIdx]);
    if (o.keyframes1based) {
      for (const f of o.keyframes1based) {
        const f0 = f - 1;
        if (startIdx <= f0 && f0 <= endIdx) keySet.add(f0);
      }
    }
    const keyIdxs = Array.from(keySet).sort((a, b) => a - b);
    if (keyIdxs.length < 2) throw new Error('关键帧数量不足，无法插值');

    const frameRange: number[] = [];
    for (let f = startIdx; f <= endIdx; f++) frameRange.push(f);

    let baseDim = 0;
    let hasQuat = false;
    if (this.trajectoryIncludesBase && this.freeJointId !== -1) {
      if (this.trajectoryBaseFormat === 'euler') baseDim = 6;
      else if (this.trajectoryBaseFormat === 'quaternion') {
        baseDim = 7;
        hasQuat = true;
      }
    }
    const baseOffset = baseDim;

    // 基座位置
    if (baseDim > 0 && (o.interpBasePos || o.interpBaseHeight)) {
      const indicesToInterp = o.interpBaseHeight && !o.interpBasePos ? [2] : [0, 1, 2];
      for (const i of indicesToInterp) {
        const vals = keyIdxs.map((k) => this.trajectoryData![k][i]);
        let interpVals: number[];
        if (keyIdxs.length >= 3) {
          const cs = naturalCubicSpline(keyIdxs, vals);
          interpVals = frameRange.map((t) => cs(t));
        } else {
          interpVals = frameRange.map((t) => linearInterp(keyIdxs, vals, t));
        }
        for (let fi = 0; fi < frameRange.length; fi++) {
          const frameIdx = frameRange[fi];
          if (!keySet.has(frameIdx)) this.trajectoryData![frameIdx][i] = interpVals[fi];
        }
      }
    }

    // 基座四元数
    if (hasQuat && o.interpBaseQuat) {
      const keyQuats: number[][] = keyIdxs.map((k) => {
        const qraw = [
          this.trajectoryData![k][3],
          this.trajectoryData![k][4],
          this.trajectoryData![k][5],
          this.trajectoryData![k][6],
        ];
        let q: number[];
        if (this.trajectoryQuatFormat === 'xyzw') q = [qraw[3], qraw[0], qraw[1], qraw[2]];
        else q = qraw;
        const qn = norm(q);
        return qn > 0 ? q.map((x) => x / qn) : [1, 0, 0, 0];
      });

      const writeQuat = (frameIdx: number, q: number[]): void => {
        if (this.trajectoryQuatFormat === 'xyzw') {
          this.trajectoryData![frameIdx][3] = q[1];
          this.trajectoryData![frameIdx][4] = q[2];
          this.trajectoryData![frameIdx][5] = q[3];
          this.trajectoryData![frameIdx][6] = q[0];
        } else {
          this.trajectoryData![frameIdx][3] = q[0];
          this.trajectoryData![frameIdx][4] = q[1];
          this.trajectoryData![frameIdx][5] = q[2];
          this.trajectoryData![frameIdx][6] = q[3];
        }
      };

      if (keyIdxs.length >= 3) {
        const outQuats = quatBatchSpline(keyIdxs, keyQuats, frameRange);
        for (let fi = 0; fi < frameRange.length; fi++) {
          const frameIdx = frameRange[fi];
          if (!keySet.has(frameIdx)) writeQuat(frameIdx, outQuats[fi]);
        }
      } else {
        for (let aIdx = 0; aIdx < keyIdxs.length - 1; aIdx++) {
          const k0 = keyIdxs[aIdx];
          const k1 = keyIdxs[aIdx + 1];
          const q0 = keyQuats[aIdx];
          const q1 = keyQuats[aIdx + 1];
          const span = k1 - k0;
          if (span <= 0) continue;
          for (let frameIdx = k0 + 1; frameIdx < k1; frameIdx++) {
            if (keySet.has(frameIdx)) continue;
            const t = (frameIdx - k0) / span;
            writeQuat(frameIdx, slerp(q0, q1, t));
          }
        }
      }
    }

    // 关节
    const jointDim = this.trajectoryData[0].length - baseOffset;
    let selectedJointUiIdxs: number[];
    if (o.selectedJointIndices == null || o.selectedJointIndices.length === 0) {
      selectedJointUiIdxs = this.trajectoryJointInfos.map((_, i) => i);
    } else {
      selectedJointUiIdxs = o.selectedJointIndices
        .map((i) => Math.trunc(i))
        .filter((i) => i >= 0 && i < this.trajectoryJointInfos.length);
    }
    const maxUiJoint = Math.min(this.trajectoryJointInfos.length, jointDim);
    selectedJointUiIdxs = selectedJointUiIdxs.filter((i) => i >= 0 && i < maxUiJoint);

    let appliedJointCount = 0;
    for (const uiIdx of selectedJointUiIdxs) {
      if (!o.interpJoints) break;
      if (uiIdx < 0 || uiIdx >= this.trajectoryJointInfos.length) continue;
      const info = this.trajectoryJointInfos[uiIdx];
      const dataIdx = baseOffset + uiIdx;
      if (dataIdx < 0 || dataIdx >= this.trajectoryData[0].length) continue;

      let vals = keyIdxs.map((k) => this.trajectoryData![k][dataIdx]);

      let jointType: number;
      try {
        jointType = Number(this.model.jnt_type[info.jointId]);
      } catch {
        jointType = -1;
      }
      const isJointLimited = info.limited;

      let isLimitedHinge = false;
      if (jointType === MJJNT_HINGE && isJointLimited) {
        const jr = info.jointRange;
        if (jr && jr.length >= 2) {
          const width = jr[1] - jr[0];
          if (width < 2 * Math.PI - 1e-6) isLimitedHinge = true;
        }
      }

      if (jointType === MJJNT_HINGE && !isLimitedHinge) vals = unwrap(vals);

      let interpVals: number[];
      if (keyIdxs.length >= 3) {
        const cs = naturalCubicSpline(keyIdxs, vals);
        interpVals = frameRange.map((t) => cs(t));
      } else {
        interpVals = new Array(frameRange.length).fill(NaN);
        for (let aIdx = 0; aIdx < keyIdxs.length - 1; aIdx++) {
          const k0 = keyIdxs[aIdx];
          const k1 = keyIdxs[aIdx + 1];
          const a0 = vals[aIdx];
          const a1 = vals[aIdx + 1];
          const span = k1 - k0;
          if (span <= 0) continue;
          if (jointType === MJJNT_HINGE && !isLimitedHinge) {
            const delta = (((a1 - a0 + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
            for (let frameIdx = k0; frameIdx <= k1; frameIdx++) {
              const t = (frameIdx - k0) / span;
              interpVals[frameIdx - frameRange[0]] = a0 + delta * t;
            }
          } else {
            for (let frameIdx = k0; frameIdx <= k1; frameIdx++) {
              interpVals[frameIdx - frameRange[0]] = linearInterp([k0, k1], [a0, a1], frameIdx);
            }
          }
        }
        for (let i = 0; i < interpVals.length; i++) {
          if (Number.isNaN(interpVals[i])) interpVals[i] = linearInterp(keyIdxs, vals, frameRange[i]);
        }
      }

      let anyChanged = false;
      for (let fi = 0; fi < frameRange.length; fi++) {
        const frameIdx = frameRange[fi];
        if (keySet.has(frameIdx)) continue;
        const v = interpVals[fi];
        let vClamped = v;
        if (isJointLimited && info.jointRange && info.jointRange.length >= 2) {
          vClamped = clip(v, info.jointRange[0], info.jointRange[1]);
        }
        if (Math.abs(this.trajectoryData![frameIdx][dataIdx] - vClamped) > 1e-12) {
          this.trajectoryData![frameIdx][dataIdx] = vClamped;
          anyChanged = true;
        }
      }
      if (anyChanged) appliedJointCount++;
    }

    if (o.interpJoints) {
      console.log(
        `[robotEdit] 插值完成: 区间 ${startIdx + 1}-${endIdx + 1}，关键帧 [${keyIdxs.map((k) => k + 1).join(',')}]，插值 ${appliedJointCount} 个关节`,
      );
    }
  }

  // ==================================================================
  // ZMP 风险检测（zmp_analysis）
  // ==================================================================

  private _calculateZmpCom(): number[] {
    if (!this.model || !this.data) return [0, 0, 0];
    let totalMass = 0;
    const comNum = [0, 0, 0];
    const bodyPositions = this._xipos.length > 0 ? this._xipos : this._xpos;
    for (let bodyId = 0; bodyId < this.model.nbody; bodyId++) {
      const mass = Number(this.model.body_mass[bodyId]);
      if (mass <= 0) continue;
      totalMass += mass;
      const off = bodyId * 3;
      comNum[0] += mass * Number(bodyPositions[off]);
      comNum[1] += mass * Number(bodyPositions[off + 1]);
      comNum[2] += mass * Number(bodyPositions[off + 2]);
    }
    if (totalMass <= 1e-10) return [0, 0, 0];
    return [comNum[0] / totalMass, comNum[1] / totalMass, comNum[2] / totalMass];
  }

  private _currentBaseRpy(): number[] {
    if (!this.data || this.freeJointQposAddr < 0) return [0, 0, 0];
    const baseAddr = this.freeJointQposAddr;
    if (baseAddr + 7 > this._qpos.length) return [0, 0, 0];
    const quat = this._readBaseQuatWxyz();
    return quaternionToEuler(quat);
  }

  private _inferFootHalfDims(): [number, number] {
    const defaultHalfL = 0.09;
    const defaultHalfW = 0.055;
    if (!this.model || !this.data) return [defaultHalfL, defaultHalfW];

    this._buildFootLevelingMap();
    let bestArea = 0;
    let bestHalfL = defaultHalfL;
    let bestHalfW = defaultHalfW;

    for (const side of ['left', 'right']) {
      const bodyId = this.footLevelingMap[side]?.footBodyId ?? -1;
      if (bodyId < 0 || bodyId >= this.model.nbody) continue;
      for (let geomId = 0; geomId < this.model.ngeom; geomId++) {
        if (Number(this.model.geom_bodyid[geomId]) !== bodyId) continue;
        const gtype = Number(this.model.geom_type[geomId]);
        const size = [
          Number(this.model.geom_size[geomId * 3]),
          Number(this.model.geom_size[geomId * 3 + 1]),
          Number(this.model.geom_size[geomId * 3 + 2]),
        ];
        let halfL: number;
        let halfW: number;
        if (gtype === 0) {
          halfL = size[0] ?? 0;
          halfW = size[1] ?? 0;
        } else if (gtype === 1) {
          const r = size[0];
          halfL = halfW = r;
        } else if (gtype === 2) {
          const r = size[0];
          const h = size[1] ?? 0;
          halfL = r + h;
          halfW = r;
        } else if (gtype === 3) {
          halfL = size[0];
          halfW = size[1] ?? halfL;
        } else if (gtype === 5) {
          halfL = size[0];
          halfW = size[1] ?? halfL;
        } else {
          continue;
        }
        const area = halfL * halfW;
        if (area > bestArea) {
          bestArea = area;
          bestHalfL = halfL;
          bestHalfW = halfW;
        }
      }
    }
    if (bestArea > 1e-8) return [bestHalfL, bestHalfW];
    return [defaultHalfL, defaultHalfW];
  }

  private _supportBoxMarginInBaseYaw(
    pointXY: number[],
    contactXY: number[][],
    yaw: number,
    footHalfLength?: number,
    footHalfWidth?: number,
  ): { margin: number; pointLocal: number[]; delta: number[]; box: [number, number, number, number] } {
    if (footHalfLength == null || footHalfWidth == null) {
      const [inferredL, inferredW] = this._inferFootHalfDims();
      if (footHalfLength == null) footHalfLength = inferredL;
      if (footHalfWidth == null) footHalfWidth = inferredW;
    }
    const contact = contactXY.map((p) => [p[0], p[1]]);
    if (contact.length === 0) {
      return { margin: -Infinity, pointLocal: [0, 0], delta: [0, 0], box: [0, 0, 0, 0] };
    }
    const p = [pointXY[0], pointXY[1]];
    const forward = [Math.cos(yaw), Math.sin(yaw)];
    const left = [-Math.sin(yaw), Math.cos(yaw)];
    const toLocal = (pt: number[]): number[] => [dot(pt, forward), dot(pt, left)];

    const pointLocal = toLocal(p);
    const contactLocal = contact.map(toLocal);
    const xs = contactLocal.map((c) => c[0]);
    const ys = contactLocal.map((c) => c[1]);
    const xmin = Math.min(...xs) - footHalfLength!;
    const xmax = Math.max(...xs) + footHalfLength!;
    const ymin = Math.min(...ys) - footHalfWidth!;
    const ymax = Math.max(...ys) + footHalfWidth!;
    const margin = Math.min(pointLocal[0] - xmin, xmax - pointLocal[0], pointLocal[1] - ymin, ymax - pointLocal[1]);
    const center = [(xmin + xmax) * 0.5, (ymin + ymax) * 0.5];
    const delta = [pointLocal[0] - center[0], pointLocal[1] - center[1]];
    return { margin, pointLocal, delta, box: [xmin, xmax, ymin, ymax] };
  }

  private _zmpDirectionLabel(localDelta: number[]): string {
    if (Math.abs(localDelta[0]) >= Math.abs(localDelta[1])) {
      return localDelta[0] > 0 ? '前倾风险' : '后退风险';
    }
    return localDelta[1] > 0 ? '左倾风险' : '右倾风险';
  }

  private _formatFrameRanges(frames: number[], maxParts = 10): string {
    const sorted = Array.from(new Set(frames.map((f) => Math.trunc(f)))).sort((a, b) => a - b);
    if (sorted.length === 0) return '无';
    const ranges: [number, number][] = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const f = sorted[i];
      if (f === prev + 1) {
        prev = f;
        continue;
      }
      ranges.push([start, prev]);
      start = f;
      prev = f;
    }
    ranges.push([start, prev]);

    const parts: string[] = [];
    for (let i = 0; i < Math.min(ranges.length, maxParts); i++) {
      const [a, b] = ranges[i];
      parts.push(a === b ? String(a + 1) : `${a + 1}-${b + 1}`);
    }
    if (ranges.length > maxParts) parts.push(`...共${ranges.length}段`);
    return parts.join(', ');
  }

  zmpRiskCheck(startFrame: number, endFrame: number): ZmpResult | null {
    console.log('[robotEdit] zmpRiskCheck start', { startFrame, endFrame });
    try {
      if (!this.trajectoryData || !this.model || !this.data) return null;

      const startIdx = Math.max(0, Math.trunc(startFrame));
      const endIdx = Math.min(Math.trunc(endFrame), this.trajectoryData.length - 1);
      if (startIdx > endIdx) return null;

      this._buildFootLevelingMap();
      const feet = this._footSpecsForScope('auto');
      if (feet.length === 0) return null;

      const frameIndices: number[] = [];
      for (let f = startIdx; f <= endIdx; f++) frameIndices.push(f);
      const total = frameIndices.length;
      if (total < 3) return null;

      const samples: Array<{ frameIdx: number; com: number[]; feet: Record<string, number[]>; rpy: number[] }> = [];
      const footZValues: number[] = [];
      for (const frameIdx of frameIndices) {
        const row = this._getFrameDisplayRow(frameIdx);
        if (row === null) continue;
        this._applyRowToDataDirect(row);

        const footPositions: Record<string, number[]> = {};
        for (const spec of feet) {
          const bodyId = spec.footBodyId;
          if (bodyId < 0 || bodyId >= this.model.nbody) continue;
          const pos = this._getXpos(bodyId).slice();
          footPositions[spec.side] = pos;
          footZValues.push(pos[2]);
        }
        samples.push({ frameIdx, com: this._calculateZmpCom(), feet: footPositions, rpy: this._currentBaseRpy() });
      }

      if (samples.length < 3 || footZValues.length === 0) return null;

      let floorZ = quantile(footZValues, 0.05);
      if (floorZ > 0.06 || floorZ < -0.06) floorZ = 0.0;

      const fps = 30.0;
      const dt = 1.0 / fps;
      const coms = samples.map((s) => s.com);
      const comsForAcc = smoothVectorsForZmp(coms);
      let vel: number[][];
      let acc: number[][];
      if (samples.length >= 3) {
        vel = gradientAxis0(comsForAcc, dt);
        acc = gradientAxis0(vel, dt);
      } else {
        acc = comsForAcc.map((r) => r.map(() => 0));
      }

      const highRisk: number[] = [];
      const warningRisk: number[] = [];
      const byDirection: Record<string, number[]> = {
        '前倾风险': [],
        '后退风险': [],
        '左倾风险': [],
        '右倾风险': [],
        'ZMP出支撑区': [],
        'COM投影出支撑区': [],
        '单脚/弱支撑': [],
      };
      const details: ZmpDetail[] = [];
      let worst: [number, ZmpDetail] | null = null;
      const g = 9.81;

      for (let i = 0; i < samples.length; i++) {
        const sample = samples[i];
        const frameIdx = sample.frameIdx;
        const feetPos = sample.feet;
        if (Object.keys(feetPos).length === 0) continue;

        const lowestZ = Math.min(...Object.values(feetPos).map((p) => p[2]));
        const contact: number[][] = [];
        for (const pos of Object.values(feetPos)) {
          const relFloor = pos[2] - floorZ;
          const relLowest = pos[2] - lowestZ;
          if (relFloor <= 0.045 && relLowest <= 0.035) contact.push([pos[0], pos[1]]);
        }
        let weakSupport = false;
        if (contact.length === 0) {
          let lowestSide = Object.keys(feetPos)[0];
          for (const side of Object.keys(feetPos)) {
            if (feetPos[side][2] <= feetPos[lowestSide][2]) lowestSide = side;
          }
          contact.push([feetPos[lowestSide][0], feetPos[lowestSide][1]]);
          weakSupport = true;
        } else if (contact.length === 1) {
          weakSupport = true;
        }

        const com = coms[i];
        const zRel = Math.max(0.05, com[2] - floorZ);
        const zmpXY = [com[0] - (zRel * acc[i][0]) / g, com[1] - (zRel * acc[i][1]) / g];
        const yaw = sample.rpy[2];
        const zmpMargin = this._supportBoxMarginInBaseYaw(zmpXY, contact, yaw);
        const comMargin = this._supportBoxMarginInBaseYaw([com[0], com[1]], contact, yaw);
        const direction = this._zmpDirectionLabel(zmpMargin.margin < comMargin.margin ? zmpMargin.delta : comMargin.delta);

        const riskReasons: string[] = [];
        let level = 'ok';

        if (zmpMargin.margin < -0.03) {
          level = 'high';
          riskReasons.push(`ZMP越出支撑区 ${Math.abs(zmpMargin.margin).toFixed(3)}m`);
          byDirection['ZMP出支撑区'].push(frameIdx);
        } else if (zmpMargin.margin < 0.015) {
          level = 'warning';
          riskReasons.push(`ZMP贴近支撑边界，余量 ${zmpMargin.margin.toFixed(3)}m`);
        }

        if (comMargin.margin < -0.025) {
          level = 'high';
          riskReasons.push(`COM投影越出支撑区 ${Math.abs(comMargin.margin).toFixed(3)}m`);
          byDirection['COM投影出支撑区'].push(frameIdx);
        } else if (comMargin.margin < 0.0 && level !== 'high') {
          level = 'warning';
          riskReasons.push(`COM投影已到边界外 ${Math.abs(comMargin.margin).toFixed(3)}m`);
        }

        if (weakSupport && (zmpMargin.margin < 0.025 || comMargin.margin < 0.015)) {
          if (level !== 'high') level = 'warning';
          riskReasons.push('单脚/弱支撑下稳定裕度较小');
          byDirection['单脚/弱支撑'].push(frameIdx);
        }

        const pitch = sample.rpy[1];
        const roll = sample.rpy[0];
        if (Math.abs(pitch) > 0.35) {
          if (level !== 'high') level = 'warning';
          riskReasons.push(`base pitch 偏大 ${((pitch * 180) / Math.PI).toFixed(1)}deg`);
        }
        if (Math.abs(roll) > 0.3) {
          if (level !== 'high') level = 'warning';
          riskReasons.push(`base roll 偏大 ${((roll * 180) / Math.PI).toFixed(1)}deg`);
        }

        if (level === 'high' || level === 'warning') {
          byDirection[direction].push(frameIdx);
          const record: ZmpDetail = {
            frame_idx: frameIdx,
            level,
            direction,
            zmp_margin: zmpMargin.margin,
            com_margin: comMargin.margin,
            zmp: zmpXY.slice(),
            com: com.slice(),
            reasons: riskReasons,
          };
          details.push(record);
          if (level === 'high') highRisk.push(frameIdx);
          else warningRisk.push(frameIdx);
          const severity = Math.min(zmpMargin.margin, comMargin.margin);
          if (worst === null || severity < worst[0]) worst = [severity, record];
        }
      }

      const highRiskSorted = Array.from(new Set(highRisk)).sort((a, b) => a - b);
      const warningRiskSorted = Array.from(new Set(warningRisk))
        .filter((f) => !highRiskSorted.includes(f))
        .sort((a, b) => a - b);
      const score = Math.max(
        0,
        Math.round(100 - (100 * highRiskSorted.length) / total - (35 * warningRiskSorted.length) / total),
      );

      let worstText: string;
      if (worst !== null) {
        const r = worst[1];
        worstText = `最危险帧: ${r.frame_idx + 1}，${r.direction}，ZMP余量 ${r.zmp_margin.toFixed(3)}m，COM余量 ${r.com_margin.toFixed(3)}m`;
      } else {
        worstText = '最危险帧: 无明显风险';
      }

      const summary = `区间 ${startIdx + 1}-${endIdx + 1} ZMP稳定性评分: ${score}/100\n高风险帧: ${highRiskSorted.length}，边界风险帧: ${warningRiskSorted.length}\n${worstText}`;

      const lines: string[] = [
        'ZMP风险检测报告（近似诊断，不修改动作）',
        `区间: ${startIdx + 1}-${endIdx + 1}`,
        `假定帧率: ${fps.toFixed(0)} FPS`,
        `估计地面高度: ${floorZ.toFixed(3)} m`,
        '计算: ZMP_xy = COM_xy - COM_z/g * COM_acc_xy',
        '',
        '问题帧段:',
      ];
      let anyGroup = false;
      for (const [label, frames] of Object.entries(byDirection)) {
        const uniq = Array.from(new Set(frames)).sort((a, b) => a - b);
        if (uniq.length === 0) continue;
        anyGroup = true;
        lines.push(`- ${label}: ${this._formatFrameRanges(uniq)}`);
      }
      if (!anyGroup) lines.push('- 未发现明显 ZMP/COM 越界风险');

      if (details.length > 0) {
        lines.push('', '具体风险帧（最多显示前30条）:');
        for (const item of details.slice(0, 30)) {
          const levelText = item.level === 'high' ? '高风险' : '边界风险';
          const reason = item.reasons.length > 0 ? item.reasons.join('；') : item.direction;
          lines.push(
            `- 第 ${item.frame_idx + 1} 帧 [${levelText}] ${item.direction}: ZMP余量 ${item.zmp_margin.toFixed(3)}m, COM余量 ${item.com_margin.toFixed(3)}m；${reason}`,
          );
        }
        if (details.length > 30) lines.push(`... 还有 ${details.length - 30} 条风险帧未展开`);
      }

      lines.push(
        '',
        '建议:',
        '- 前倾/后退风险: 优先检查该帧前后 base pitch、COM 是否冲出脚底支撑区。',
        '- 左/右倾风险: 优先检查 base roll、支撑脚是否单脚、双脚间距是否过窄。',
        '- ZMP越界但COM未越界: 多半是动作加速度过大，可先做局部降速或插帧平滑。',
        '- COM投影越界: 静态姿态本身就危险，单纯平滑通常不够，需要改脚步或重编舞。',
      );
      const report = lines.join('\n');

      console.log('[robotEdit] ' + summary.replace(/\n/g, '；'));
      return {
        score,
        high_risk: highRiskSorted,
        warning_risk: warningRiskSorted,
        by_direction: byDirection,
        details,
        worst_text: worstText,
        summary,
        report,
      };
    } finally {
      console.log('[robotEdit] zmpRiskCheck end');
    }
  }
}
